import { useState } from 'react';
import { Wrench, ChevronDown, ChevronRight, Terminal, Search, FileText, Edit, Globe } from 'lucide-react';

/**
 * ToolCallBadge — AI 工具呼叫
 *   - 預覽用 parseToolInput 從 JSON input 抓出 tool-specific summary（command/pattern/file_path…），
 *     不直接秀 raw JSON
 *   - 點開展開：input + result 完整內容
 */
export function ToolCallBadge({ tool }) {
    const [open, setOpen] = useState(false);
    const parsedInput = parsePartialJson(tool.input);
    const { icon: Icon, label, preview } = summarize(tool.name, parsedInput);
    const hasResult = tool.result !== null && tool.result !== undefined;

    return (
        <div className="my-2 rounded-md border border-cream-200 bg-white text-xs overflow-hidden">
            <button
                onClick={() => setOpen((o) => !o)}
                className="w-full px-3 py-1.5 flex items-center gap-1.5 text-ink-700 hover:bg-cream-50 transition"
            >
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <Icon size={12} strokeWidth={1.8} className="text-accent-700 shrink-0" />
                <span className="font-mono font-medium shrink-0">{label}</span>
                <span className="text-ink-500 truncate font-mono">
                    {preview}
                </span>
                <span className={['ml-auto text-[10px] shrink-0', hasResult ? 'text-success-500' : 'text-ink-400'].join(' ')}>
                    {hasResult ? '✓' : '…'}
                </span>
            </button>
            {open && (
                <div className="px-3 pb-2 space-y-2">
                    {parsedInput && (
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-0.5">input</div>
                            <pre className="text-[11px] whitespace-pre-wrap break-words bg-cream-50 p-2 rounded border border-cream-200 max-h-48 overflow-y-auto">
                                {JSON.stringify(parsedInput, null, 2)}
                            </pre>
                        </div>
                    )}
                    {hasResult && (
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-0.5">result</div>
                            <pre className="text-[11px] whitespace-pre-wrap break-words bg-cream-50 p-2 rounded border border-cream-200 max-h-64 overflow-y-auto">
                                {String(tool.result)}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/** Tolerant JSON parser for partial / streaming JSON */
function parsePartialJson(s) {
    if (!s) return null;
    try {
        return JSON.parse(s);
    } catch {
        // try fixing trailing partials
        for (let i = s.length; i > 0; i--) {
            try { return JSON.parse(s.slice(0, i)); } catch {}
        }
        return null;
    }
}

/** Map tool name + parsed input → { icon, label, preview text } */
function summarize(name, input) {
    const args = input || {};
    switch (name) {
        case 'Bash':
            return { icon: Terminal, label: 'Bash', preview: args.command || args.description || '' };
        case 'Read':
            return { icon: FileText, label: 'Read', preview: args.file_path || '' };
        case 'Write':
            return { icon: Edit, label: 'Write', preview: args.file_path || '' };
        case 'Edit':
            return { icon: Edit, label: 'Edit', preview: args.file_path || '' };
        case 'Glob':
            return { icon: Search, label: 'Glob', preview: args.pattern || '' };
        case 'Grep':
            return { icon: Search, label: 'Grep', preview: `${args.pattern || ''} ${args.path ? `in ${args.path}` : ''}`.trim() };
        case 'WebFetch':
        case 'WebSearch':
            return { icon: Globe, label: name, preview: args.url || args.query || '' };
        case 'Skill':
            return { icon: Wrench, label: 'Skill', preview: args.skill || '' };
        default:
            // 嘗試找一個關鍵欄位顯示
            const key = ['name', 'path', 'file_path', 'command', 'query', 'url', 'pattern', 'skill'].find(k => args[k]);
            return { icon: Wrench, label: name, preview: (key ? args[key] : '') || '' };
    }
}
